import { DirectionsBoatFilled } from '@mui/icons-material'
import { Box, styled } from '@mui/material'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useBulkAssignResources } from 'src/api/scheduledTask'
import { invalidateTaskCache } from 'src/api/tasks'
import { TaskTypes } from 'src/enums'
import { useDialogCtx } from 'src/hooks/context-hooks'
import AssignResourceForm from 'src/screens/customer/tasks/tasks/AssignResourceForm'
import { GridActionButton } from '../common/GridActionButton'
import { TaskCalendarEvent } from './VesselTasksCalendarDialog'

interface BulkAssignVesselsProps {
  selectedTasks: TaskCalendarEvent[]
  customerId: number
  onAssignCallback: () => void
}

const BulkAssignVessels = ({ selectedTasks, customerId, onAssignCallback }: BulkAssignVesselsProps) => {
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation()
  const { mutateAsync: mutateBulkAssignResources } = useBulkAssignResources(customerId)

  const onSubmit = async (form: any, pop: () => void) => {
    form.tasks = []
    form.scheduledTaskIds = []
    selectedTasks.forEach(task => {
      if (task.id > 0) {
        form.scheduledTaskIds.push(task.id)
      }
      form.tasks.push({
        TaskId: task.task.id,
        startDate: task.startDate,
        endDate: task.endDate,
      })
    })
    await mutateBulkAssignResources(form, {
      onSuccess: () => {
        toast.success(t('successfully-assigned-all-work-orders', { ns: 'common' }))
        invalidateTaskCache.useGetFilterCalendarScheduleTask(customerId)
        onAssignCallback()
        pop()
      },
      onError: (error: any) => {
        if (error.statusText == 'Conflict') {
          error.data.forEach((item: any) => {
            const startDate = moment(item.conflictStartDate).format('DD/MM/YYYY')
            const endDate = moment(item.conflictEndDate).format('DD/MM/YYYY')
            toast.error(
              t('resource-conflict-unable-to-assign-resource-to-work-order', {
                ns: 'task',
                taskName: item.taskName,
                startDate,
                endDate,
              }),
              { autoClose: false },
            )
          })
          selectedTasks
            .filter(task => !error.data.some(item => item.taskId == task.task.id))
            .forEach(task => {
              const startDate = moment(task.startDate).format('DD/MM/YYYY')
              const endDate = moment(task.endDate).format('DD/MM/YYYY')
              toast.success(
                t('resource-assigned-assign-resource-to-work-order', {
                  ns: 'task',
                  taskTitle: task.task.title,
                  startDate,
                  endDate,
                }),
              )
            })
        }
        invalidateTaskCache.useGetFilterCalendarScheduleTask(customerId)
        onAssignCallback()
        pop()
      },
    })
  }

  const handleBulkAssignVessels = () => {
    showDialog(AssignResourceForm, {
      title: t('assign-resource', { ns: 'task' }),
      componentProps: {
        onSubmit: (form, customerId, pop) => onSubmit(form, pop),
        customerId: customerId,
        task: {
          id: 0,
          type: TaskTypes.Once,
        },
      },
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
    })
  }

  return (
    <ActionContainer>
      <GridActionButton
        buttonIcon={<DirectionsBoatFilled />}
        height="44px"
        width="44px"
        toolTipTitle={
          selectedTasks.length > 0 ? '' : t('ctrl-select-multiple-work-orders-to-assign-vessels', { ns: 'task' })
        }
        onClick={selectedTasks.length > 0 ? handleBulkAssignVessels : undefined}
        disabled={selectedTasks.length == 0}
      />
    </ActionContainer>
  )
}

export default BulkAssignVessels

const ActionContainer = styled(Box)({
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'end',
  paddingBottom: '5px',
  overflow: 'visible',
  zIndex: 1000,
})
